/* eslint-disable */
import * as React from 'react';
import { Suspense, lazy } from 'react';
import { inject, observer } from 'mobx-react';
import AppStore, { ContentType } from './appStore';
import './appContainerStyles.scss';
// The shell offset for the fixed side nav rail has to be in the main bundle: the
// rail itself is lazy-loaded, and the page must not be laid out under it in the
// meantime.
import './sideNav/appSideNavStyles.scss';
import AppTopNav, { TOP_NAV_CONTAINER_ID } from './topNav/appTopNav';
import { CookieStore } from '../../cookiesStore/cookieStore';
import LoadingSpinner from 'components/loadingSpinner/loadingSpinner';
import { action, observable } from 'mobx';
import DocSplitPanel from 'pages/document/components/splitPanel/docSplitPanel';
import AppLayout from '@amzn/awsui-components-react/polaris/app-layout';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { getChatbotEnabled } from 'api/graphql/query';
import { PolicyAssistantFab } from '../../components/genAI/policyAssistant/PolicyAssistantFab';
import { agenticSurfaceStore } from '../../components/genAI/policyAssistant/agenticSurfaceStore';

const GRCAIAssistant = lazy(() => import('@amzn/grc-ai-assistant-frontend'));
const Breadcrumb = lazy(() => import('./breadcrumb/breadcrumb'));
const Feedback = lazy(() => import('../../components/feedback/feedback'));
const AppSideNav = lazy(() => import('./sideNav/appSideNav'));

/**
 * TEMPORARY UAT toggle for the chat assistant.
 * true  → legacy GRCAIAssistant FAB (current default until the new experience is UAT'd)
 * false → new in-package Policy Assistant FAB
 * Remove this flag and the legacy branch once the new chat option passes UAT.
 */
const USE_LEGACY_CHAT_ASSISTANT = true;

interface Props {
  appStore?: AppStore;
  children: any;
}

@inject('appStore') @observer
export class AppContainer extends React.PureComponent<Props, unknown> {


  @observable spliPanelClose: Boolean = true;
  @observable chatbotEnabled: boolean = true; // Default to true to avoid flickering
  @observable showChatbotAlert: boolean = false;

  constructor(props: Props) {
    super(props);
    const { appStore } = this.props;
    appStore!.load();
  }

  /**
   * Handles actions for clicks on the AppLayout content.
   */
  handleAppLayoutClick(): void {
    const { appStore } = this.props;

    // Hide the feedback component for outside clicks.
    if (appStore?.isFeedbackVisible) appStore.showFeedback(false, true);
  }

  /**
   * Handles fetching chatbot enabled status
   */
  componentDidMount(): void {
    const { appStore } = this.props;
    
    appStore?.api.query(getChatbotEnabled, {}).then(action((enabledResult) => {
      // Process chatbot enabled status
      if (enabledResult && enabledResult.data && typeof enabledResult.data.chatbotEnabled === 'boolean') {
        this.chatbotEnabled = enabledResult.data.chatbotEnabled;
      }

      // Show alert if chatbot is disabled
      if (!this.chatbotEnabled) {
        this.showChatbotAlert = true;
        // Auto-hide alert after 10 seconds
        setTimeout(action(() => {
          this.showChatbotAlert = false;
        }), 10000);
      }
    })).catch((error) => {
      console.error("Error fetching chatbot data: ", error);
      // Default to enabled on error (fail-safe)
      this.chatbotEnabled = true;
    });
  }

  /**
   * Classes that reserve the width of the fixed side nav rail for the page shell.
   * @returns {string} Class list for the element wrapping AppLayout.
   */
  get shellClassName(): string {
    const { appStore } = this.props;
    const classes = ['policy--jupiter-shell'];

    if (appStore?.hideSideNav) {
      classes.push('policy--jupiter-shell--nav-hidden');
    } else if (appStore?.isSideNavOpen) {
      classes.push('policy--jupiter-shell--nav-expanded');
    }
    return classes.join(' ');
  }

  // See Fixed Top Navigation section:
  // https://polaris.a2z.com/components/awsui-top-navigation/?example=with-utility-controls&tabId=api
  render(): any {
    const { children, appStore } = this.props;
    const breadcrumbItems = appStore?.breadcrumbItems || [];
    const disableContentPaddings = (appStore?.useDocumentLayout && appStore?.contentType !== ContentType.WIZARD) || appStore?.contentType === ContentType.DASHBOARD;

    return (
      <div className="awsui">
        {/* <AnnotationContextLayer appStore={appStore} data={appStore?.tutorialData}> */}
        {/*
          * Rendered without a wrapper on purpose. AppTopNav's own container is the
          * sticky element (via the global-nav package's `.top-navigation` rule), and a
          * sticky box can only travel within its parent's box — so wrapping it in a
          * div that is exactly the bar's height pins it to nothing and it scrolls away
          * with the page. As a direct child of this full-height root it sticks to the
          * viewport. AppTopNav owns its own outside-click handling for the feedback
          * popover.
          */}
        <AppTopNav />
        {this.showChatbotAlert && (
          <div style={{ 
            position: 'fixed', 
            // Clears the top nav bar ($jupiter-topnav-height in _jupiterNav.scss).
            top: '60px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 1003, 
            maxWidth: '400px',
            width: '100%',
            padding: '0 20px'
          }}>
            <Alert
              type="info"
              dismissible
              onDismiss={action(() => this.showChatbotAlert = false)}
              header="Chatbot Temporarily Disabled"
            >
              The AI assistant chatbot is currently disabled. Please check back later.
            </Alert>
          </div>
        )}
        {/* The rail is a fixed sibling of AppLayout rather than its `navigation`
            slot: JupiterSideNav positions itself under the top nav and owns its
            collapsed/expanded widths, so the shell below reserves that width with a
            margin instead. */}
        {!appStore?.hideSideNav && (
          <Suspense fallback={<></>}>
            <AppSideNav />
          </Suspense>
        )}
        {appStore!.loading ? <LoadingSpinner size="large" absoluteCenter /> : (
          <div className={this.shellClassName}>
          <AppLayout
            headerVariant="high-contrast"
            disableContentPaddings={disableContentPaddings}
            className="policy--app"
            contentType={appStore!.contentType}
            content={
              (appStore?.useDocumentLayout) || appStore!.contentType === ContentType.WIZARD ? (
                <>
                  {/*
                    * Plain, non-sticky breadcrumbs for the review surfaces. This used to
                    * be a sticky `.custom-home__header-sticky` band with its own
                    * `min-height` and `top` offset, which sat between the top nav and the
                    * page header and produced the gap beneath the nav.
                    *
                    * The document page does not come through here at all — it renders its
                    * breadcrumbs inside the HeroHeader's `breadcrumbs` slot, so the band
                    * meets the nav flush. The review and transclude-bulk-review pages have
                    * no hero, so they still need these; without them they would have no
                    * breadcrumbs at all.
                    */}
                  {appStore!.useStickyBreadcrumbs && breadcrumbItems?.length > 1 && (
                    <div className="policy--review-breadcrumbs">
                      <Suspense fallback={<></>}>
                        <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                      </Suspense>
                    </div>
                  )}
                  {children}
                </>
              ) : (
                <section onClick={() => this.handleAppLayoutClick()} style={{ padding: appStore?.useDocumentLayout ? '1rem 4rem' : '0rem 0rem' }}>
                  {breadcrumbItems.length > 1 && appStore?.useDocumentLayout && (
                    <Suspense fallback={<></>}>
                      <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                    </Suspense>
                  )}
                  {children}
                </section>
              )
            }
            breadcrumbs={
              breadcrumbItems.length > 1 && !appStore?.useDocumentLayout && (
                <Suspense fallback={<></>}>
                  <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                </Suspense>
              )
            }
            headerSelector={`#${TOP_NAV_CONTAINER_ID}`}
            toolsHide={appStore?.toolsHide ?? true}
            toolsOpen={appStore?.toolsOpen ?? false}
            tools={appStore?.toolBarContent}
            // Navigation lives in the fixed Jupiter rail rendered above, so
            // Cloudscape's own drawer stays hidden on every page.
            navigationHide
            onToolsChange={() => {
              appStore?.setToolsHide();
              appStore?.setToolsOpen();
            }}
            toolsWidth={appStore?.toolBarWidth}
            splitPanel={<DocSplitPanel appStore={appStore} />}
            splitPanelOpen={appStore!.splitPanelOpen}
            splitPanelPreferences={{ position: 'side' }}
            onSplitPanelToggle={action(e => {
              const isOpen = e.detail.open;
        
              if (isOpen) {
                appStore!.setSplitPanel(true);
                appStore!.setSplitPanelOpen(true);
              } else {
                appStore!.setSplitPanelOpen(false);
              }
            })}
          />
          </div>
        )}
        {appStore?.isFeedbackVisible && (
          <Suspense fallback={<></>}>
            <Feedback appStore={appStore} defaultOpen primary position="top-right" />
          </Suspense>
        )}
        <CookieStore ref={appStore?.cookiesStore} didMount={() => appStore?.checkCookiesStore()} />
        {/* </AnnotationContextLayer> */}
        {/* Chat assistant — legacy GRCAIAssistant by default; the new in-package Policy
            Assistant once the UAT toggle is flipped. See USE_LEGACY_CHAT_ASSISTANT.
            The legacy FAB keeps its server kill-switch (chatbotEnabled) and additionally
            hides on agentic surfaces so it never overlaps a dedicated agentic chat. The
            new FAB self-hides on agentic surfaces internally. */}
        {USE_LEGACY_CHAT_ASSISTANT ? (
          <div className='grc-assistant-wrapper'>
            {this.chatbotEnabled && !agenticSurfaceStore.isActive && (
              <Suspense fallback={<></>}>
                <GRCAIAssistant apps="policy" searchFolder={appStore?.getParentFolder} />
              </Suspense>
            )}
          </div>
        ) : (
          <PolicyAssistantFab
            userName={appStore?.currentUserID}
            getPageContext={() => {
              const crumbs = (appStore?.breadcrumbItems || [])
                .map((b: any) => b?.label)
                .filter((l: any): l is string => typeof l === 'string' && l.trim().length > 0);
              const doc = appStore?.docSideNavStore?.docPageStore?.document as any;
              return {
                breadcrumb: crumbs.join(' > ') || undefined,
                documentTitle: doc?.title,
                documentType: doc?.documentType,
                documentId: doc?.id || doc?.documentId,
              };
            }}
          />
        )}
      </div>
    );
  }
}

export default AppContainer;
